// Copyright (c) Microsoft. All rights reserved.

import { IUploadInfo } from '../../components/chat/tabs/DDQTab';
import { BaseService } from './BaseService';

export interface IFundInfo {
    id: string;
    title: string;
    default?: string;
}

export interface IDocumentImportProgress {
    inProgress: boolean;
}

export interface IQuestionsExtractionResponse {
    statusQueryGetUri: string;
    documentName: string;
    sessionId: string;
}

export interface IQuestionsStatusResponse {
    status: IQuestionsExtractionStatusInformation;
    documentName: string;
    viewQuestionsUri: string;
}

export interface IQuestionsExtractionStatusInformation {
    name: string;
    instanceId: string;
    runtimeStatus: string;
    output: number;
    createdTime: string;
    lastUpdatedTime: string;
}

interface IQuestionsStatusRequest {
    chatId: string;
    userId: string;
    currentStatusQueryGetUri?: string;
    currentDocumentName?: string;
    currentSessionId?: string;
}

export class DocumentImportService extends BaseService {
    public importDocumentAsync = async <T>(
        commandPath: string,
        userId: string,
        userName: string,
        chatId: string,
        documents: File[],
        accessToken: string,
        uploadInfo?: IUploadInfo,
    ) => {
        const formData = new FormData();
        formData.append('userId', userId);
        formData.append('userName', userName);
        formData.append('chatId', chatId);
        formData.append('documentScope', 'Chat');

        if (uploadInfo) {
            formData.append('ddqPrompt', uploadInfo.ddqPrompt ?? '');
            formData.append('fund', uploadInfo.selectedFund ?? '');
        }

        for (const document of documents) {
            formData.append('formFiles', document);
        }

        return await this.getResponseAsync<T>(
            {
                commandPath,
                method: 'POST',
                body: formData,
            },
            accessToken,
        );
    };

    public checkQuestionExtractionStatusAsync = async <T>(
        chatId: string,
        userId: string,
        currentStatusQueryGetUri: string,
        currentDocumentName: string,
        currentSessionId: string,
        accessToken: string,
    ) => {
        const body: IQuestionsStatusRequest = {
            chatId,
            userId,
            currentStatusQueryGetUri,
            currentDocumentName,
            currentSessionId,
        };

        return await this.getResponseAsync<T>(
            {
                commandPath: 'checkQuestionsExtractionStatus',
                method: 'POST',
                body: body,
            },
            accessToken,
        );
    };

    public getFundsAsync = async (accessToken: string): Promise<IFundInfo[]> => {
        const result = await this.getResponseAsync<IFundInfo[]>(
            {
                commandPath: "getFunds",
                method: 'GET',
            },
            accessToken,
        );

        return result;
    };
}
